import {
    InfiniteQueryConfig,
    PaginatedQueryConfig,
    QueryConfig,
    QueryFunction,
    QueryKey,
    useInfiniteQuery,
    usePaginatedQuery,
    useQuery,
    useQueryCache,
} from 'react-query';
import { getEventsById } from '../api';
import { FormattedEvent } from '../types';

const DEFAULT_QUERY_OPTIONS = {
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
};

interface SharedUseEventsOptions {
    expandEventsOnMount?: boolean;
    customExpandFunc?(): Promise<{ events: FormattedEvent[] }>;
}

type FormattedEventResponse = {
    events: FormattedEvent[];
    [key: string]: any;
};

interface UseEventsOptions
    extends SharedUseEventsOptions,
        QueryConfig<FormattedEventResponse> {}

interface UseInfiniteEventsOptions
    extends InfiniteQueryConfig<FormattedEventResponse>,
        SharedUseEventsOptions {}

interface UsePaginatedEventsOptions
    extends PaginatedQueryConfig<FormattedEventResponse>,
        SharedUseEventsOptions {}

const expandEvents = (
    data: FormattedEventResponse,
): Promise<{ events: FormattedEvent[] }> => {
    const { events } = data;
    const eventIds = events.map(({ id }) => id);

    return getEventsById(eventIds);
};

/**
 *
 * @param key
 * @param data
 * @param options
 *
 * Util which handles leverages useQuery to expand
 * the passed in events against the destination
 * API to ensure fully fledged event cards.
 */
const useExpandEventsOnMount = (
    key: QueryKey,
    data?: FormattedEventResponse,
    options?: UseEventsOptions,
) => {
    const customExpandFunc = options?.customExpandFunc;
    const expandFunc = customExpandFunc || expandEvents;

    const queryData = useQuery(
        [key, 'mount-expand'],
        () => expandFunc(data as FormattedEventResponse),
        {
            ...DEFAULT_QUERY_OPTIONS,
            ...options,
        },
    );

    return queryData;
};

/**
 *
 * @param key
 * @param ids
 * @param options
 *
 * Thin wrapper around `useQuery`
 * https://react-query.tanstack.com/docs/api#usequery
 *
 * Defaults to calling `destination/events/` with preconfigured
 * expansions.
 *
 */
export const useEventsById = (
    key: QueryKey,
    ids: string[],
    options?: UseEventsOptions,
) => {
    const queryData = useQuery(key, () => getEventsById(ids), {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    });

    return queryData;
};

/**
 *
 * @param key
 * @param asyncFunction
 * @param options
 *
 * Thin wrapper around `useQuery`
 * https://react-query.tanstack.com/docs/api#usequery
 *
 */
export const useEvents = (
    key: QueryKey,
    asyncFunction: QueryFunction<FormattedEventResponse>,
    options?: UseEventsOptions,
) => {
    const queryCache = useQueryCache();
    const expandEventOnMount = options?.expandEventsOnMount;

    const updateQueryCacheData = (key: QueryKey, expandedEvents: any) => {
        const currentData = queryCache.getQueryData<FormattedEventResponse>(
            key,
        );
        const newData = {
            ...currentData,
            events: expandedEvents,
        };
        queryCache.setQueryData(key, newData);

        return newData;
    };

    const { data, ...otherValues } = useQuery(key, asyncFunction, {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    });

    useExpandEventsOnMount(key, data, {
        enabled:
            !!options?.initialData &&
            !otherValues.isFetchedAfterMount &&
            expandEventOnMount,
        onSuccess: ({ events: expandedEvents }) => {
            updateQueryCacheData(key, expandedEvents);
        },
        customExpandFunc: options?.customExpandFunc,
    });

    return {
        data,
        ...otherValues,
    };
};

/**
 * WIP: Behaviors specific to the return types of usePaginatedQuery
 * are still being finalized. So this is not yet exported or ready
 * for consumption.
 *
 * @param key
 * @param asyncFunction
 * @param options
 *
 * Thin wrapper around `usePaginatedQuery`
 * https://react-query.tanstack.com/docs/api#usepaginatedquery
 *
 */
const usePaginatedEvents = (
    key: QueryKey,
    asyncFunction: QueryFunction<FormattedEventResponse>,
    options?: UsePaginatedEventsOptions,
) => {
    const queryCache = useQueryCache();

    const { resolvedData, latestData, ...otherValues } = usePaginatedQuery(
        key,
        asyncFunction,
        {
            ...DEFAULT_QUERY_OPTIONS,
            ...options,
        },
    );

    return {
        resolvedEvents: resolvedData,
        latestEvents: latestData,
        ...otherValues,
    };
};

/**
 * Same as usePaginatedEvents but returns all
 * previously returned pages along with the next one
 *
 * Useful for the "show more" or "infinite loading" patterns as opposed to
 * class "next page" approach
 *
 * Thin wrapper around `useInfiniteQuery`
 * https://react-query.tanstack.com/docs/api#useinfinitequery
 */
export const useInfiniteEvents = (
    key: QueryKey,
    asyncFunction: QueryFunction<FormattedEventResponse>,
    options?: UseInfiniteEventsOptions,
) => {
    const queryCache = useQueryCache();

    const updateInfiniteQueryCache = (key: QueryKey, expandedEvents: any) => {
        //sets "successful fetch" internal variable
        //thereby triggering onSettled/onSuccess handlers
        queryCache.setQueryData<FormattedEventResponse[]>(
            key,
            (oldData) => {
                const latestData = oldData?.pop();

                return [
                    ...oldData,
                    {
                        ...latestData,
                        events: expandedEvents,
                    },
                ];
            },
            { ...DEFAULT_QUERY_OPTIONS },
        );
    };

    const { data, ...otherValues } = useInfiniteQuery(key, asyncFunction, {
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
    });

    useExpandEventsOnMount(key, data?.[0], {
        enabled:
            !!options?.initialData &&
            !otherValues.isFetchedAfterMount &&
            options?.expandEventsOnMount,
        onSuccess: ({ events: expandedEvents }) => {
            updateInfiniteQueryCache(key, expandedEvents);
        },
        customExpandFunc: options?.customExpandFunc,
    });

    return {
        data,
        ...otherValues,
    };
};
